import React, { useEffect, useMemo, useState } from 'react';
import { Card, Grid, Snackbar, Alert } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import PropTypes from 'prop-types';
import ArgonTypography from 'components/ArgonTypography';
import ArgonSelect from 'components/ArgonSelect';
import { useNotifications } from 'context/NotificationContext';

function StageEnvironment({ envs, selectedEnv, selected, onVersionTypeChange, initialVersionType }) {
  const versionTypeOptions = useMemo(
    () => [
      { value: 'patch', label: 'Change' },
      { value: 'new', label: 'New Version' },
    ],
    []
  );

  const [value, setValue] = useState('');
  const [options, setOptions] = useState(null);
  const [renderOptions, setRender] = useState(false);
  const [versionType, setVersionType] = useState(initialVersionType || '');
  const [envError, setEnvError] = useState('');
  const [versionTypeError, setVersionTypeError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const { addNotification } = useNotifications();

  useEffect(() => {
    if (envs && envs.length > 0) {
      let envOptions = envs.map((org) => ({
        value: org.org_id, // The value is the org_id
        label: org.name,
      }));

      console.log('[envOptions]', envOptions);
      setOptions(envOptions);
      setRender(true);

      // The `selected` prop is the full org object
      const initialEnvOrgId = selected?.org_id;
      if (initialEnvOrgId && envs.some((env) => env.org_id === initialEnvOrgId)) {
        setValue(initialEnvOrgId);
      } else {
        setValue('');
      }
    } else {
      // ...
    }
  }, [envs, selected, initialVersionType]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleEnvChange = (e) => {
    const newEnvOrgId = e.target.value;
    setValue(newEnvOrgId);

    // Find the full org object that corresponds to the selected org_id
    const selectedOrgObject = envs.find((env) => env.org_id === newEnvOrgId);

    // Pass the entire object up to the parent
    selectedEnv(selectedOrgObject);

    if (!newEnvOrgId) {
      const errorMsg = 'Please select an environment.';
      setEnvError(errorMsg);
      addNotification({ title: ['Error', 'Environment Selection'], severity: 'error', details: errorMsg });
      showSnackbar(errorMsg, 'error');
    } else {
      setEnvError('');
      const selectedLabel = options.find((opt) => opt.value === newEnvOrgId)?.label || newEnvOrgId;
      const successMsg = `Environment selected: ${selectedLabel}`;
      addNotification({ title: ['Info', 'Environment Changed'], severity: 'info', details: successMsg });
    }
    setVersionTypeError('');
  };

  const handleVersionTypeChange = (e) => {
    const newVersionType = e.target.value;
    setVersionType(newVersionType);
    onVersionTypeChange(newVersionType);
    if (!newVersionType) {
      const errorMsg = 'Please select a version type.';
      setVersionTypeError(errorMsg);
      addNotification({ title: ['Error', 'Version Type Selection'], severity: 'error', details: errorMsg });
      showSnackbar(errorMsg, 'error');
    } else {
      setVersionTypeError('');
      const selectedLabel = versionTypeOptions.find((opt) => opt.value === newVersionType)?.label || newVersionType;
      const successMsg = `Version type set to: ${selectedLabel}`;
      addNotification({ title: ['Info', 'Version Type Changed'], severity: 'info', details: successMsg });
    }
    setEnvError('');
  };

  return (
    <ArgonBox py={3}>
      <ArgonBox mb={3}>
        <Card>
          <ArgonBox px={4} py={4} mb={3}>
            <Grid container spacing={3} direction="column">
              <Grid item>
                <ArgonBox px={2} py={2} mb={3} display="flex" alignItems="center" gap={2}>
                  <ArgonTypography sx={{ fontSize: '12pt' }} fontWeight="regular">
                    Select the environment:
                  </ArgonTypography>
                  {renderOptions && (
                    <ArgonSelect
                      placeholder="Select Environment"
                      value={value}
                      onChange={handleEnvChange}
                      options={options || []}
                    />
                  )}
                  {envError && (
                    <ArgonTypography variant="caption" color="error">
                      {envError}
                    </ArgonTypography>
                  )}
                </ArgonBox>
              </Grid>
              <Grid item>
                <ArgonBox px={2} py={2} mb={3} display="flex" alignItems="center" gap={2}>
                  <ArgonTypography sx={{ fontSize: '12pt' }} fontWeight="regular">
                    Select Version Type:
                  </ArgonTypography>
                  <ArgonSelect
                    placeholder="Select Type"
                    value={versionType}
                    onChange={handleVersionTypeChange}
                    options={versionTypeOptions}
                  />
                  {versionTypeError && (
                    <ArgonTypography variant="caption" color="error">
                      {versionTypeError}
                    </ArgonTypography>
                  )}
                </ArgonBox>
              </Grid>
            </Grid>
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          variant="filled"
          severity={snackbarSeverity}
          sx={{ alignItems: 'center', alignContent: 'center' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </ArgonBox>
  );
}

StageEnvironment.propTypes = {
  envs: PropTypes.array.isRequired,
  selectedEnv: PropTypes.func.isRequired,
  selected: PropTypes.object,
  onVersionTypeChange: PropTypes.func.isRequired,
  initialVersionType: PropTypes.string,
};

export default StageEnvironment;
